import { addHours, differenceInHours } from 'date-fns';
import {
  ColorStop,
  WeatherStationHistoricalData,
  WeatherStationObservation,
  WeatherStationObservationDataKey,
} from 'features/weatherStations/weatherStations.types';
import { ReactNode } from 'react';
import { formatDate } from 'shared/dates';
import { TableCellProps } from '@mui/material';
import {
  EMPTY_DATA_VALUE,
  TEMPERATURE_COLOR_STOPS,
} from 'features/weatherStations/weatherStations.constants';
import {
  OBSERVATION_SENSOR_TYPE_TO_LABEL,
  OBSERVATIONS_DATA_PARSERS,
} from './WeatherStationDetails.constants';

export const getLatestObservation = (
  observations: WeatherStationObservation[],
): WeatherStationObservation | null => {
  if (observations.length === 0) return null;

  // Sort observations by timestamp (newest first)
  const sortedObs = [...observations].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );

  return sortedObs[0];
};

/**
 * Generates an array of Date objects representing a range of hours between
 * the given start and end dates, incremented by the specified step in hours.
 *
 * Note: The resulting array purposely does NOT include the startDate itself.
 * Each element is calculated as startDate + (stepHours * n), where n starts at 1.
 */
const generateHourlyRange = (
  startDate: Date,
  stepHours: number,
  endDate: Date,
): Date[] => {
  const totalHours = differenceInHours(endDate, startDate);
  const steps = Math.floor(totalHours / stepHours);
  return Array.from({ length: steps }, (_, i) =>
    addHours(startDate, (i + 1) * stepHours),
  );
};

const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return [r, g, b];
};

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((x) =>
      Math.round(Math.max(0, Math.min(255, x)))
        .toString(16)
        .padStart(2, '0')
        .toUpperCase(),
    )
    .join('')}`;

// Linearly interpolates between two hex colors based on the given ratio (0 to 1)
const interpolateColor = (
  color1: string, // color in hex format
  color2: string, // color in hex format
  ratio: number, // interpolation ratio (0 = color1, 1 = color2)
): string => {
  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  const r = r1 + (r2 - r1) * ratio;
  const g = g1 + (g2 - g1) * ratio;
  const b = b1 + (b2 - b1) * ratio;

  return rgbToHex(r, g, b);
};

const tempToColor = (temp: number | null): string => {
  if (typeof temp !== 'number' || Number.isNaN(temp)) return 'transparent';

  const stops = TEMPERATURE_COLOR_STOPS;
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  // Exact match
  for (const stop of stops) {
    if (stop.temp === temp) return stop.color;
  }

  // Out-of-range bounds
  if (temp <= firstStop.temp) return firstStop.color;
  if (temp >= lastStop.temp) return lastStop.color;

  // Interpolated color between stops
  for (let i = 1; i < stops.length; i += 1) {
    const prev = stops[i - 1];
    const next = stops[i];

    if (temp < next.temp) {
      const ratio = (temp - prev.temp) / (next.temp - prev.temp);
      return interpolateColor(prev.color, next.color, ratio);
    }
  }

  // Fallback (should never be hit)
  return lastStop.color;
};

const parseTemp = (temp: string): number | null => {
  const match = temp.match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
};

export const tempsToLinearGradient = (temps: string[]): string | undefined => {
  const parsedTemps = temps.filter((temp) => temp !== EMPTY_DATA_VALUE);

  if (!parsedTemps.length) return undefined;

  const stops = parsedTemps.map((t, i) => {
    const tempNum = parseTemp(t);
    const color = tempToColor(tempNum);
    const pos = Math.round((i / (parsedTemps.length - 1)) * 100);
    return `${color} ${pos}%`;
  });

  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

// Calculates the midpoint color from an array of hex colors
const getMidpointColor = (colors: string[]): string => {
  if (colors.length === 1) {
    return colors[0];
  }

  const mid = (colors.length - 1) / 2;
  const lower = Math.floor(mid);
  const upper = Math.ceil(mid);

  // If midpoint lands exactly on a color
  if (lower === upper) {
    return colors[lower];
  }

  const ratio = mid - lower;
  return interpolateColor(colors[lower], colors[upper], ratio);
};

// Calculates Euclidean distance between two RGB colors
const calculateRgbDistance = (
  rgb1: [number, number, number],
  rgb2: [number, number, number],
): number => {
  return Math.sqrt(
    (rgb1[0] - rgb2[0]) ** 2 +
      (rgb1[1] - rgb2[1]) ** 2 +
      (rgb1[2] - rgb2[2]) ** 2,
  );
};

// Finds the closest color stop using Euclidean distance in RGB space
const findClosestColorStop = (
  targetRgb: [number, number, number],
): ColorStop | null => {
  const stops = TEMPERATURE_COLOR_STOPS;

  let closest = stops[0];
  let minDistance = calculateRgbDistance(targetRgb, hexToRgb(closest.color));

  for (let i = 1; i < stops.length; i += 1) {
    const stop = stops[i];
    const stopRgb = hexToRgb(stop.color);
    const distance = calculateRgbDistance(targetRgb, stopRgb);

    if (distance < minDistance) {
      minDistance = distance;
      closest = stop;
    }
  }

  return closest;
};

/**
 * Determines the appropriate contrast text color for a given gradient
 * by analyzing the middle color and finding the closest temperature color stop
 */
const getContrastTextColor = (gradient: string): string => {
  const colors = gradient.match(/#[0-9a-fA-F]{6}/g);
  if (!colors || colors.length === 0) return 'text.primary';

  try {
    const midHex = getMidpointColor(colors);

    // Check for exact match with a color stop
    const exactStop = TEMPERATURE_COLOR_STOPS.find(
      (stop) => stop.color === midHex,
    );
    if (exactStop) {
      return exactStop.contrastTextColor;
    }

    // Fallback to closest RGB match
    const midRgb = hexToRgb(midHex);
    const closestColorStop = findClosestColorStop(midRgb);
    return closestColorStop?.contrastTextColor || 'text.primary';
  } catch (err) {
    return 'text.primary';
  }
};

const getTableCellSx = (input: {
  values: ReactNode[];
  type: WeatherStationObservationDataKey;
  value: ReactNode;
  index: number;
}): TableCellProps['sx'] | undefined => {
  const { values, type, value, index } = input;
  if (type !== 'airTemp') {
    return undefined;
  }
  const prev = values[index - 1] as string | undefined;
  // Ensure the gradient has at least two colors by repeating `value`.
  const temps = [prev, value, value].filter(Boolean) as string[];
  const gradient = tempsToLinearGradient(temps);
  return {
    background: gradient,
    color: gradient ? getContrastTextColor(gradient) : undefined,
  };
};

/**
 * Generates historical weather data for a set of observations, grouped into
 * hourly intervals starting from a given date. The intervals are determined
 * by the specified step (in hours), and the startFrom date is purposely
 * excluded from the resulting range.
 *
 * Each interval contains the most recent observation within that period,
 * parsed and formatted according to the observation value type.
 */
export const getObservationsHistoricalData = (
  observations: WeatherStationObservation[],
  startFrom: Date,
  step: number,
): WeatherStationHistoricalData | null => {
  if (observations.length === 0) return null;

  const hourlyDateRange = generateHourlyRange(startFrom, step, new Date());

  if (hourlyDateRange.length === 0) return null;

  const relevantObservations = observations.filter(
    (obs) => obs.timestamp.getTime() >= startFrom.getTime(),
  );

  const head = hourlyDateRange.map((date) => formatDate(date, 'h a'));

  const dataValues = (
    Object.entries(OBSERVATION_SENSOR_TYPE_TO_LABEL) as [
      WeatherStationObservationDataKey,
      ReactNode,
    ][]
  ).map(([key, unit]) => {
    const values = hourlyDateRange.map((date, index) => {
      const prevDate = index === 0 ? startFrom : hourlyDateRange[index - 1];

      const observationsInInterval = relevantObservations.filter(
        (obs) =>
          obs.timestamp.getTime() > prevDate.getTime() &&
          obs.timestamp.getTime() <= date.getTime(),
      );

      const latestObservation = getLatestObservation(observationsInInterval);
      const latestObservationValue = latestObservation?.[key];
      const value = OBSERVATIONS_DATA_PARSERS[key]?.(latestObservationValue);

      return { type: key, value: value ?? EMPTY_DATA_VALUE };
    });

    return [{ type: key, value: unit }, ...values];
  });

  const data = dataValues.map((row) => {
    const [labelCell, ...dataCells] = row;
    const values = dataCells.map((cell) => cell.value);

    return [labelCell].concat(
      dataCells.map((cell, index) => {
        const type = cell.type as WeatherStationObservationDataKey;
        const { value } = cell;

        const tableCellSx = getTableCellSx({
          values,
          type,
          value,
          index,
        });

        return { type, value, tableCellSx };
      }),
    );
  });

  return { head: ['', ...head], data };
};
